import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, Sparkles, Users, Search, Filter, Plus, Globe, Star } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import AmbassadorCard from '../components/AmbassadorCard';

const AmbassadorDiscovery = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [ambassadors, setAmbassadors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [favorites, setFavorites] = useState(new Set());
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [talentType, setTalentType] = useState(searchParams.get('type') || 'ai'); // all, human, ai
  const [category, setCategory] = useState(searchParams.get('category') || 'all');
  const [tier, setTier] = useState(searchParams.get('tier') || 'all');
  const [language, setLanguage] = useState(searchParams.get('language') || 'all');

  useEffect(() => {
    fetchCategories();
    fetchAmbassadors();
    if (user) {
      fetchFavorites();
    }
  }, [category, tier, language]);

  useEffect(() => {
    // Update URL params when filters change
    const params = new URLSearchParams();
    if (talentType !== 'all') params.set('type', talentType);
    if (category) params.set('category', category);
    if (tier) params.set('tier', tier);
    if (language) params.set('language', language);
    setSearchParams(params);
  }, [talentType, category, tier, language]);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/ambassadors/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  const fetchAmbassadors = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category && category !== 'all') params.append('category', category);
      if (tier && tier !== 'all') params.append('tier', tier);
      if (language && language !== 'all') params.append('language', language);
      
      const response = await api.get(`/ambassadors?${params.toString()}`);
      setAmbassadors(response.data.ambassadors);
    } catch (error) {
      console.error('Failed to load ambassadors:', error);
      toast.error('Failed to load AI Ambassadors');
    } finally {
      setLoading(false);
    }
  };

  const fetchFavorites = async () => {
    try {
      const response = await api.get('/ambassadors/favorites/list');
      setFavorites(new Set(response.data.map(a => a.id)));
    } catch (error) {
      console.error('Failed to load favorites:', error);
    }
  };

  const handleToggleFavorite = async (ambassadorId, shouldFavorite) => {
    if (!user) {
      toast.error('Please log in to save favorites');
      navigate('/login');
      return;
    }

    try {
      if (shouldFavorite) {
        await api.post('/ambassadors/favorites', { ambassador_id: ambassadorId });
        setFavorites(prev => new Set([...prev, ambassadorId]));
        toast.success('Added to favorites');
      } else {
        await api.delete(`/ambassadors/favorites/${ambassadorId}`);
        setFavorites(prev => {
          const next = new Set(prev);
          next.delete(ambassadorId);
          return next;
        });
        toast.success('Removed from favorites');
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      toast.error('Failed to update favorites');
    }
  };

  const clearFilters = () => {
    setCategory('all');
    setTier('all');
    setLanguage('all');
  };

  const handleTalentTypeChange = (type) => {
    setTalentType(type);
    if (type === 'human') {
      // Navigate to human talent discovery
      navigate('/discover');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ambassador-discovery-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button 
            variant="ghost" 
            onClick={() => navigate(user?.role === 'client' ? '/client/home' : '/')}
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">AI Ambassadors</h1>
              <p className="text-muted-foreground">Premium AI-powered brand representatives</p>
            </div>
          </div>
        </div>

        {/* Talent Type Toggle */}
        <Card className="bg-white mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">Talent Type</span>
              </div>
              <Tabs value={talentType} onValueChange={handleTalentTypeChange} className="w-full sm:w-auto">
                <TabsList className="grid w-full sm:w-auto grid-cols-3">
                  <TabsTrigger value="all" data-testid="filter-all">
                    All Talent
                  </TabsTrigger>
                  <TabsTrigger value="human" data-testid="filter-human">
                    <Users className="w-4 h-4 mr-1" />
                    Human
                  </TabsTrigger>
                  <TabsTrigger value="ai" data-testid="filter-ai">
                    <Sparkles className="w-4 h-4 mr-1" />
                    AI Ambassadors
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </CardContent>
        </Card>

        {/* Filters */}
        <Card className="bg-white mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium mb-1 block">Category</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="category-filter">
                    <SelectValue placeholder="All Categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {categories.map((cat) => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label} ({cat.count})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex-1">
                <label className="text-sm font-medium mb-1 block">Tier</label>
                <Select value={tier} onValueChange={setTier}>
                  <SelectTrigger data-testid="tier-filter">
                    <SelectValue placeholder="All Tiers" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tiers</SelectItem>
                    <SelectItem value="standard">Standard ($399+)</SelectItem>
                    <SelectItem value="premium">Premium ($599+)</SelectItem>
                    <SelectItem value="luxury">Luxury ($999+)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex-1">
                <label className="text-sm font-medium mb-1 block">Language</label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger data-testid="language-filter">
                    <SelectValue placeholder="All Languages" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Languages</SelectItem>
                    <SelectItem value="English">English</SelectItem>
                    <SelectItem value="Spanish">Spanish</SelectItem>
                    <SelectItem value="French">French</SelectItem>
                    <SelectItem value="Mandarin">Mandarin</SelectItem>
                    <SelectItem value="Portuguese">Portuguese</SelectItem>
                    <SelectItem value="German">German</SelectItem>
                    <SelectItem value="Italian">Italian</SelectItem>
                    <SelectItem value="Japanese">Japanese</SelectItem>
                    <SelectItem value="Arabic">Arabic</SelectItem>
                    <SelectItem value="Russian">Russian</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {(category !== 'all' || tier !== 'all' || language !== 'all') && (
                <div className="flex items-end">
                  <Button 
                    variant="outline" 
                    onClick={clearFilters}
                    data-testid="clear-filters-btn"
                  >
                    Clear Filters
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Custom Ambassador CTA */}
        {user?.role === 'client' && (
          <Card className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white mb-6">
            <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <Plus className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Create Custom AI Ambassador</h3>
                  <p className="text-white/90 text-sm">
                    Build a unique AI representative tailored to your brand • Starting at $1,500
                  </p>
                </div>
              </div>
              <Button 
                className="bg-white text-emerald-600 hover:bg-white/90"
                onClick={() => navigate('/client/custom-ambassador')}
                data-testid="custom-ambassador-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Request Custom
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
          </div>
        ) : ambassadors.length === 0 ? (
          <Card className="bg-white">
            <CardContent className="py-12 text-center">
              <Sparkles className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No AI Ambassadors Found</h3>
              <p className="text-muted-foreground mb-4">
                Try adjusting your filters or create a custom ambassador
              </p>
              <Button variant="outline" onClick={clearFilters}>
                Clear All Filters
              </Button>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground">
                Showing {ambassadors.length} AI Ambassador{ambassadors.length !== 1 ? 's' : ''}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="ambassadors-grid">
              {ambassadors.map((ambassador) => (
                <AmbassadorCard
                  key={ambassador.id}
                  ambassador={ambassador}
                  isFavorited={favorites.has(ambassador.id)}
                  onToggleFavorite={handleToggleFavorite}
                  showFavoriteButton={!!user}
                />
              ))}
            </div>
          </>
        )}

        {/* Info Section */}
        <Card className="bg-white mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              Why Choose AI Ambassadors?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Globe className="w-6 h-6 text-purple-600" />
                </div>
                <h4 className="font-medium mb-2">Multilingual Excellence</h4>
                <p className="text-sm text-muted-foreground">
                  Communicate with global audiences in multiple languages with native fluency
                </p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Sparkles className="w-6 h-6 text-pink-600" />
                </div>
                <h4 className="font-medium mb-2">Consistent Brand Voice</h4>
                <p className="text-sm text-muted-foreground">
                  Perfect delivery every time with precise messaging aligned to your brand
                </p>
              </div>
              <div className="text-center p-4">
                <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Star className="w-6 h-6 text-cyan-600" />
                </div>
                <h4 className="font-medium mb-2">Premium Innovation</h4>
                <p className="text-sm text-muted-foreground">
                  Position your brand at the forefront of technology and innovation
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AmbassadorDiscovery;
